import React from 'react';

/**
 * Generic expandable card for the Data page sections.
 *
 * Props:
 *   title         — Main label text
 *   subtitle      — Optional secondary text below the title
 *   badge         — Text shown in the lime pill badge (e.g. "12 Students")
 *   badgeColor    — Optional override for badge background (default: var(--accent-green))
 *   accentColor   — Left border accent colour (default: var(--accent-green))
 *   headerActions — Optional nodes (buttons, etc.) rendered beside the badge.
 *                   Rendered outside the header <button> so they can be
 *                   interactive controls of their own.
 *   expanded      — Boolean; whether the card is currently expanded
 *   onToggle      — Callback fired when the card header is clicked
 *   children      — Nested content shown when expanded
 */
const DataCard = ({
  title,
  subtitle,
  badge,
  badgeColor,
  accentColor = 'var(--accent-green)',
  headerActions,
  expanded = false,
  onToggle,
  children,
}) => {
  return (
    <div
      className={`data-card theme-card${expanded ? ' data-card--expanded' : ''}`}
      style={{ borderLeft: `3px solid ${accentColor}` }}
    >
      {/* Header row — always visible. The toggle is a button; headerActions sit
          beside it rather than inside, since buttons cannot be nested. */}
      <div className="data-card-header-row">
        <button
          className="data-card-header"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <div className="data-card-title-group">
            <span className="data-card-title">{title}</span>
            {subtitle && (
              <span className="data-card-subtitle">{subtitle}</span>
            )}
          </div>
        </button>

        <div className="data-card-actions">
          {headerActions}

          {badge !== undefined && badge !== null && (
            <span
              className="data-card-badge"
              style={badgeColor ? { backgroundColor: badgeColor, color: '#000' } : {}}
            >
              {badge}
            </span>
          )}

          <button
            className="data-card-chevron-button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
          >
            <i
              className={`bi bi-chevron-down data-card-chevron${expanded ? ' rotated' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && children && (
        <div className="data-card-body">
          {children}
        </div>
      )}
    </div>
  );
};

export default DataCard;
